import { customerValidationDeprecated } from "@talladega/helpers";
import { usePostalCodeMask } from "@talladega/hooks";
import { Province, BaseInputProps as InputProps } from "@talladega/types";
import { useTranslation } from "next-i18next";
import { ChangeEvent, forwardRef } from "react";
import {
  Path,
  RegisterOptions,
  UseFormRegister,
  FieldValues,
} from "react-hook-form";
import { Form } from ".";

type Props = Omit<InputProps, "maxLength" | "minLength">;

export const PostalCodeInput = forwardRef<HTMLInputElement, Props>(
  ({ id = "postalCode", defaultValue, onChange, ...props }: Props, ref) => {
    const { t } = useTranslation(["common"]);
    const { maskPipe: maskPostalCodePipe, unMaskPipe: unMaskPostalCodePipe } =
      usePostalCodeMask();

    const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
      const unmasked = unMaskPostalCodePipe(e.target.value);
      const masked = maskPostalCodePipe(unmasked);
      e.target.value = masked;

      if (onChange) onChange(e);
    };

    return (
      <Form.Input
        label={t("common:postal_code") || undefined}
        id={id}
        autoComplete="postal-code"
        minLength={7}
        maxLength={7}
        defaultValue={maskPostalCodePipe(defaultValue ?? "")}
        onChange={handleOnChange}
        ref={ref}
        {...props}
      />
    );
  }
);

type HookFormProps<T extends FieldValues> = {
  register: UseFormRegister<T>;
  fieldKey: Path<T>;
  registerOptions?: RegisterOptions;
  provinceToValidate?: Province;
  requried?: boolean;
};

type WithHookFormProps<T extends FieldValues> = Props & HookFormProps<T>;

const postalCodeValidation = customerValidationDeprecated.postalCode;

export const PostalCodeInputWithValidation = <T extends FieldValues>({
  register,
  fieldKey,
  registerOptions,
  provinceToValidate,
  required = false,
  ...props
}: WithHookFormProps<T>) => {
  const { t } = useTranslation(["common"]);
  return (
    <PostalCodeInput
      suppressHotjar
      {...props}
      {...register(fieldKey, {
        required: {
          value: required,
          message: t("common:required"),
        },
        maxLength: postalCodeValidation.maxLength,
        validate: {
          matchingProvince: (value) =>
            postalCodeValidation.validateWithProvince(
              value as string,
              provinceToValidate
            ) ||
            t("common:postal_code_and_province_mismatch") ||
            undefined,
        },
        /**
         * This is a valid prop, but for w/e reason, eslint doesn't like it.
         */
        /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
        // @ts-ignore
        pattern: {
          value: postalCodeValidation.pattern,
          message: t("common:invalid_postal_code"),
        },
        ...registerOptions,
      })}
    />
  );
};
